// =====================================================================
// BoardFormDialog (Quadros Q1 + PRO / S59-S60) — cria um quadro com TEMPLATE
// clínico (1-clique), cor de fundo e cards-semente.
// =====================================================================
import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, CircularProgress, Box, Typography
} from "@material-ui/core";
import { toast } from "react-toastify";
import {
  createBoard, createCard, boardErrorMessage, BOARD_TYPES, BOARD_TEMPLATES, BOARD_BACKGROUNDS, BOARD_GRADIENTS
} from "../../services/boardService";

const BoardFormDialog = ({ open, onClose, onSaved }) => {
  const [templateKey, setTemplateKey] = useState("blank");
  const [name, setName] = useState("");
  const [type, setType] = useState("task");
  const [description, setDescription] = useState("");
  const [background, setBackground] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTemplateKey("blank"); setName(""); setType("task"); setDescription(""); setBackground(""); }
  }, [open]);

  const pickTemplate = (key) => {
    setTemplateKey(key);
    const tpl = BOARD_TEMPLATES.find((t) => t.key === key);
    if (tpl) {
      setType(tpl.type);
      if (tpl.key !== "blank") setName((prev) => (prev.trim() ? prev : tpl.name));
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Dê um nome ao quadro."); return; }
    setSaving(true);
    try {
      const board = await createBoard({ name, type, description, background, color: background || undefined });
      const tpl = BOARD_TEMPLATES.find((t) => t.key === templateKey);
      if (tpl && tpl.seed && tpl.seed.length && board && Array.isArray(board.lists)) {
        const lists = [...board.lists].sort((a, b) => Number(a.position) - Number(b.position));
        // eslint-disable-next-line no-restricted-syntax
        for (const s of tpl.seed) {
          const list = lists[s.list];
          // eslint-disable-next-line no-await-in-loop
          if (list) { try { await createCard(list.id, { title: s.title }); } catch { /* ignora seed */ } }
        }
      }
      toast.success("Quadro criado.");
      onSaved && onSaved(board);
    } catch (err) {
      toast.error(boardErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Novo quadro</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary">Modelo</Typography>
        <Box display="flex" style={{ gap: 6, flexWrap: "wrap", marginTop: 4, marginBottom: 8 }}>
          {BOARD_TEMPLATES.map((t) => (
            <Button key={t.key} size="small"
              variant={templateKey === t.key ? "contained" : "outlined"}
              color={templateKey === t.key ? "primary" : "default"}
              onClick={() => pickTemplate(t.key)}>
              <span style={{ marginRight: 4 }}>{t.icon}</span>{t.name}
            </Button>
          ))}
        </Box>
        <TextField label="Nome" value={name} onChange={(e) => setName(e.target.value)} fullWidth margin="dense" autoFocus
          placeholder="ex.: Tarefas da recepção" />
        <TextField select label="Tipo" value={type} onChange={(e) => setType(e.target.value)} fullWidth margin="dense"
          helperText={type === "pipeline" ? "Colunas de funil (Captação→Retorno)." : "Quadro de tarefas (A Fazer→Fazendo→Concluído)."}>
          {BOARD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">Fundo do quadro</Typography>
          <Box display="flex" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            {BOARD_GRADIENTS.map((g) => (
              <div key={g.key} onClick={() => setBackground(background === g.key ? "" : g.key)} title={g.key}
                style={{ width: 44, height: 26, borderRadius: 6, cursor: "pointer", backgroundImage: g.css,
                  border: background === g.key ? "3px solid #000" : "2px solid #fff", boxShadow: "0 0 0 1px #ccc" }} />
            ))}
            {BOARD_BACKGROUNDS.map((c) => (
              <div key={c} onClick={() => setBackground(background === c ? "" : c)} title={c}
                style={{ width: 26, height: 26, borderRadius: "50%", background: c, cursor: "pointer",
                  border: background === c ? "3px solid #000" : "2px solid #fff", boxShadow: "0 0 0 1px #ccc" }} />
            ))}
          </Box>
        </Box>
        <TextField label="Descrição (opcional)" value={description} onChange={(e) => setDescription(e.target.value)}
          fullWidth margin="dense" multiline rows={2} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}>Criar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoardFormDialog;
